
import recaptcha from './recaptcha-wrapper';
import {
    defineComponent,
    onBeforeMount,
    onMounted,
    ref,
    Ref,
    computed,
    h, nextTick,
} from 'vue';
import serverContextStore from '@/shared/content/server-context.store';

export interface RecaptchaComponentInstance extends HTMLElement {
    reset: () => void,
    execute: () => void,
}

export default defineComponent({
    name: 'Recaptcha',
    props: {
        // eslint-disable-next-line vue/require-default-prop
        theme: {
            type: String,
        },
        // eslint-disable-next-line vue/require-default-prop
        badge: {
            type: String,
            default: 'bottomleft',
        },
        // eslint-disable-next-line vue/require-default-prop
        type: {
            type: String,
        },
        // eslint-disable-next-line vue/require-default-prop
        size: {
            type: String,
            default: 'invisible',
        },
        // eslint-disable-next-line vue/require-default-prop
        tabindex: {
            type: String,
        },
        loadRecaptchaScript: {
            type: Boolean,
            default: true,
        },
        recaptchaScriptId: {
            type: String,
            default: '__RECAPTCHA_SCRIPT',
        },
        recaptchaHost: {
            type: String,
            default: 'www.google.com',
        },
        language: {
            type: String,
            default: '',
        },
    },
    emits: ['update:modelValue', 'verify', 'expired', 'error', 'render'],
    expose: ['reset', 'execute'],
    setup(props, { emit, slots }) {
        const root: Ref<HTMLElement | null> = ref(null);
        const widgetId: Ref = ref(null);
        const siteKey = computed(() => {
            return serverContextStore.recaptchaKey;
        });
        const emitVerify = (key) => {
            emit('update:modelValue', key);
            nextTick(() => emit('verify', key));
        };
        const emitExpired = () => {
            emit('expired');
        };
        const emitError = () => {
            emit('error');
        };
        const reset = () => {
            recaptcha.reset(widgetId.value);
        };
        const execute = () => {
            recaptcha.execute(widgetId.value);
        };
        onBeforeMount(() => {
            if (props.loadRecaptchaScript) {
                if (!document.getElementById(props.recaptchaScriptId)) {
                    // Note: vueRecaptchaApiLoaded load callback name is per the latest documentation
                    const script = document.createElement('script');
                    script.id = props.recaptchaScriptId;
                    script.src = `https://${props.recaptchaHost}/recaptcha/api.js?onload=vueRecaptchaApiLoaded&render=explicit&hl=${props.language}`;
                    script.async = true;
                    script.defer = true;

                    document.head.appendChild(script);
                }
            }
        });
        onMounted(() => {
            recaptcha.checkRecaptchaLoad();
            const opts = {
                ...props,
                sitekey: siteKey.value,
                callback: emitVerify,
                'expired-callback': emitExpired,
                'error-callback': emitError,
            };
            const container = slots.default ? root.value.children[0] : root.value;
            recaptcha.render(container, opts, id => {
                widgetId.value = id;
                emit('render', id);
            });
        });
        return function renderRecaptcha() {
            if (!('setErrors' in this)) {
                this.reset = reset;
                this.execute = execute;
            }
            return h('div', { ref: root }, slots.default ? slots.default() : slots.default);
        };
    },
});
